import React, { memo, useState } from "react"
import { makeStyles } from "@material-ui/core/styles"
import styles from "./ParaText.module.css"
import {
  Popper,
  ClickAwayListener,
  Fade,
  Paper,
  Typography,
  Button,
  Popover,
} from "@material-ui/core"
import { recordEvent } from "../../apis/googleAnalytics"
import { sortGapTestamentData } from "../../scripts/sortGapTestamentData"

const useStyles = makeStyles(theme => ({
  typography: {
    padding: theme.spacing(2),
    fontSize: "small",
    color: "white",
  },
  paper: {
    background: "#3A4C7D",
    width: "fit-content",
    maxWidth: "60vw",
  },

  purpleButton: {
    color: "purple",
    backgroundColor: "#ffabff",
    borderColor: "purple",
    margin: "2px",
  },
  yellowButton: {
    color: "#382a00",
    backgroundColor: "#ffff91",
    borderColor: "darkgrey",
    margin: "2px",
  },
  inactiveButton: {
    color: "grey",
    backgroundColor: "lightgrey",
    borderColor: "grey",
    margin: "2px",
    fontStyle: "italic",
  },
  popper: {
    padding: "10px",
  },
  popperTitles: {
    margin: 0,
    fontSize: "70%",
    textDecoration: "underline",
  },
}))

const Leaf = memo(
  ({
    leaf,
    language,
    version,
    standardVerse,
    versificationType,
    availableBooks,
    navigateToRef,
    translate,
    displayRef,
    showQA,
    showGAP,
  }) => {
    const classes = useStyles()
    const [anchorEl, setAnchorEl] = useState(null)
    const { tag, note, text, ref, embeddedDoc, SVitalics } = leaf
    const isVerseRef = tag && tag.indexOf("v") !== -1
    const isChapterRef = tag && tag.indexOf("c") !== -1
    let GapButton = null
    let GapVerse = null

    if (isVerseRef && showQA) {
      // check for Gap Testament links
      const GapTestamentData =
        versificationType === "eng"
          ? require("../../data/completeRefList.json")
          : require("../../data/completeRefListStandard.json")
      const GapObj = {
        quotations: [],
        allusions: [],
        color: null,
      }
      ref.forEach(r => {
        const refSplit = r.split("-")
        const GapData =
          GapTestamentData[`${refSplit[0]} ${refSplit[1]}:${refSplit[2]}`]
        if (GapData) {
          GapData.quotations.forEach(q => GapObj.quotations.push(q))
          GapData.allusions.forEach(a => GapObj.allusions.push(a))
        }
      })
      GapObj.quotations = [...new Set(GapObj.quotations)]
      GapObj.allusions = [...new Set(GapObj.allusions)]
      if (GapObj.quotations.length > 0 && GapObj.allusions.length > 0) {
        GapObj.color = "both"
      } else if (GapObj.quotations.length > 0) {
        GapObj.color = "#ffabff"
      } else if (GapObj.allusions.length > 0) {
        GapObj.color = "#fffc4f"
      }
      GapButton = GapObj.color ? GapObj : null
    }

    if (!isVerseRef && !isChapterRef && showGAP) {
      // check for Gap Testament links
      const GapTestamentData =
        versificationType === "eng"
          ? require("../../data/completeRefList.json")
          : require("../../data/completeRefListStandard.json")
      const GapObj = {
        quotations: [],
        allusions: [],
        color: null,
      }
      ref.forEach(r => {
        const refSplit = r.split("-")
        const GapData =
          GapTestamentData[`${refSplit[0]} ${refSplit[1]}:${refSplit[2]}`]
        if (GapData) {
          GapData.quotations.forEach(q => GapObj.quotations.push(q))
          GapData.allusions.forEach(a => GapObj.allusions.push(a))
        }
      })
      GapObj.quotations = [...new Set(GapObj.quotations)]
      GapObj.allusions = [...new Set(GapObj.allusions)]
      if (GapObj.quotations.length > 0) {
        GapVerse = "quotation"
      } else if (GapObj.allusions.length > 0) {
        GapVerse = "allusion"
      }
    }

    const open = Boolean(anchorEl)
    let tags = []
    if (tag) {
      tags.push(styles[tag])
    }

    if (embeddedDoc === true) {
      tags.push(styles.embeddedDoc)
    }

    if (SVitalics) {
      tags.push(styles.SVitalics)
    }

    if (GapVerse === "quotation") {
      tags.push(styles.gapQuotation)
    }

    if (GapVerse === "allusion") {
      tags.push(styles.gapAllusion)
    }

    let classNameString = ""
    tags.forEach(t => (classNameString += ` ${t}`))

    const handleClick = event => {
      recordEvent("Show Footnote", `${language}-${version}`, ref[0])
      setAnchorEl(anchorEl ? null : event.currentTarget)
    }

    const handleClickAway = () => {
      setAnchorEl(null)
    }

    //PopOver Layers toggling open and closed
    const [OTNTPopup, updateOTNTPopup] = React.useState(null)

    const OTNThandleClick = event => {
      updateOTNTPopup(OTNTPopup ? null : event.currentTarget)
    }

    const OTNTHandleClose = () => {
      updateOTNTPopup(null)
    }

    const OTNTOpen = Boolean(OTNTPopup)
    const OTNTId = OTNTOpen ? "simple-popover" : undefined

    if (note) {
      let noteText = ""
      note.children.forEach(t => (noteText += t.text))
      return (
        <span
          className={classNameString}
          verseref={`ref-${ref[0]}`}
          data-cy="leaf"
          onClick={handleClick}
          onMouseOver={handleClick}
          onMouseLeave={handleClick}
        >
          {text}
          <Popper
            id="footnote"
            open={open}
            anchorEl={anchorEl}
            transition
            placement="top"
          >
            {({ TransitionProps }) => (
              <ClickAwayListener onClickAway={handleClickAway}>
                <Fade {...TransitionProps} timeout={350}>
                  <Paper className={classes.paper}>
                    <Typography className={classes.typography}>
                      {noteText}
                    </Typography>
                  </Paper>
                </Fade>
              </ClickAwayListener>
            )}
          </Popper>
        </span>
      )
    }

    if (GapButton) {
      const { color, quotations, allusions } = GapButton
      return (
        <span
          className={classNameString}
          verseref={`ref-${ref[0]}`}
          data-cy="leaf"
          onClick={event => OTNThandleClick(event)}
          style={{
            display: displayRef ? "" : "none",
            background:
              color === "both"
                ? `repeating-linear-gradient(#fffc4f, #fffc4f 49.9%, #ffabff 50.1%, #ffabff 100%)`
                : "",
            backgroundColor: color ? color : "",
            borderRadius: color ? "20%" : "",
            paddingLeft: "3px",
          }}
        >
          {text}
          <Popover
            id={OTNTId}
            open={OTNTOpen}
            anchorEl={OTNTPopup}
            onClose={OTNTHandleClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "center",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "center",
            }}
          >
            <div className={classes.popper}>
              {quotations.length > 0 ? (
                <p className={classes.popperTitles}>
                  {translate("UI", "otnt_quotations")}
                </p>
              ) : null}
              {sortGapTestamentData(quotations).map(QLink => {
                const firstVerseQRef = QLink.split("-")[0]
                const book = translate("Books", `abbrev_${QLink.split(" ")[0]}`)
                const reference = QLink.split(" ")[1]
                  .replace(":", ": ")
                  .replace(",", ", ")
                const engBook = QLink.split(" ")[0]
                const refSplitBook = firstVerseQRef.split(" ")[0]
                const refSplitRef = firstVerseQRef.split(" ")[1].split(":")
                const navRef = {
                  book: refSplitBook,
                  chapter: refSplitRef[0],
                  verse: refSplitRef[1],
                }
                // const isActive = (state.AvailableContent.content[state.language][state.version][OTNTRefList[firstVerseQRef].book] && state.AvailableContent.content[state.language][state.version][OTNTRefList[firstVerseQRef].book].segments.length > 1) ? true : false
                return (
                  <Button
                    size="small"
                    className={
                      availableBooks.indexOf(engBook) !== -1
                        ? classes.purpleButton
                        : classes.inactiveButton
                    }
                    onClick={
                      availableBooks.indexOf(engBook) !== -1
                        ? () => navigateToRef(navRef, OTNTHandleClose)
                        : null
                    }
                    style={{ direction: language === "ARB" ? "rtl" : "ltr" }}
                    key={firstVerseQRef}
                  >
                    {book}{" "}
                    {language === "ARB"
                      ? reference.replace(":", ": ").replace(",", ", ")
                      : reference}
                  </Button>
                )
              })}
              {allusions.length > 0 ? (
                <p className={classes.popperTitles}>
                  {translate("UI", "otnt_allusions")}
                </p>
              ) : null}
              {sortGapTestamentData(allusions).map(ALink => {
                const firstVerseARef = ALink.split("-")[0]
                const book = translate("Books", `abbrev_${ALink.split(" ")[0]}`)
                const reference = ALink.split(" ")[1]
                const engBook = ALink.split(" ")[0]
                const refSplitBook = firstVerseARef.split(" ")[0]
                const refSplitRef = firstVerseARef.split(" ")[1].split(":")
                const navRef = {
                  book: refSplitBook,
                  chapter: refSplitRef[0],
                  verse: refSplitRef[1],
                }
                // const isActive = (state.AvailableContent.content[state.language][state.version][OTNTRefList[firstVerseARef].book] && state.AvailableContent.content[state.language][state.version][OTNTRefList[firstVerseARef].book].segments.length > 1) ? true : false
                return (
                  <Button
                    size="small"
                    className={
                      availableBooks.indexOf(engBook) !== -1
                        ? classes.yellowButton
                        : classes.inactiveButton
                    }
                    onClick={
                      availableBooks.indexOf(engBook) !== -1
                        ? () => navigateToRef(navRef, OTNTHandleClose)
                        : null
                    }
                    style={{ direction: language === "ARB" ? "rtl" : "ltr" }}
                    key={firstVerseARef}
                  >
                    {book}{" "}
                    {language === "ARB"
                      ? reference.replace(":", ": ").replace(",", ", ")
                      : reference}
                  </Button>
                )
              })}
            </div>
          </Popover>
        </span>
      )
    }

    if (tag && (tag.indexOf("v") !== -1 || tag.indexOf("c") !== -1)) {
      return (
        <span
          className={classNameString}
          verseref={`ref-${ref[0]}`}
          data-cy="leaf"
          style={{
            display: displayRef ? "" : "none",
          }}
        >
          {text}
        </span>
      )
    }

    return (
      <span
        className={classNameString}
        verseref={`ref-${ref[0]}`}
        data-cy="leaf"
      >
        {text}
      </span>
    )
  }
)

export default Leaf
